<script setup lang="ts">
import { computed, watch, ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from './stores/useAuthStore'
import { useAppStore } from './stores/useAppStore'
import { useVaccineDefinitions } from './stores/useVaccineDefinitions'
import { useNotificationStore } from './stores/useNotificationStore'
import { useAnnouncementStore } from './stores/useAnnouncementStore'
import BottomTabBar from './components/BottomTabBar.vue'
import LoadingScreen from './components/LoadingScreen.vue'
import AnnouncementBanner from './components/AnnouncementBanner.vue'
import {
  rescheduleAllNotifications,
  getNotificationPermission
} from './services/notificationService'
import { initializeNotificationChannel } from './services/localNotificationService'
import { createNotificationPermissionResumeHandler } from './services/notificationPermissionLifecycle'
import { getVaccineDetails, getDoseDetails } from './services/scheduleService'
import { getDisplayPlannedDate } from './utils/vaccineDate'
import { App } from '@capacitor/app'
import { LocalNotifications } from '@capacitor/local-notifications'
import { Capacitor } from '@capacitor/core'

const authStore = useAuthStore()
const appStore = useAppStore()
const vaccineDefinitions = useVaccineDefinitions()
const notificationStore = useNotificationStore()
const announcementStore = useAnnouncementStore()
const route = useRoute()
const router = useRouter()

const authReady = computed(() => authStore.ready)

// 控制「雲端資料是否已初始化完成」
const appReady = ref(false)

// 疫苗資料更新狀態
const showUpdateNotification = ref(false)
const updateNotificationMessage = ref('')

// 判斷是否顯示 bottom tab bar（只在 timeline 和 settings 頁面顯示）
const showTabBar = computed(() => {
  return ['/timeline', '/settings'].includes(route.path)
})

// 當 Auth ready 時，初始化雲端資料
watch(
  authReady,
  async (ready) => {
    if (ready && !appReady.value) {
      try {
        // 1. 先初始化疫苗定義資料（立即從快取載入，背景檢查更新）
        await vaccineDefinitions.init()

        // 2. 再初始化使用者資料
        await appStore.initFromCloud()

        // 3. 初始化通知渠道（Android）
        await initializeNotificationChannel()

        // 4. 初始化推播設定
        await notificationStore.loadSettings()
        const permission = await getNotificationPermission()
        notificationStore.updatePermission(permission)

        // 5. 一次性遷移：流感疫苗日期對齊流感季（v1.2.2）
        const FLU_MIGRATION_KEY = 'flu_season_migration_done'
        if (!localStorage.getItem(FLU_MIGRATION_KEY) && appStore.babies.length > 0) {
          await appStore.updateVaccineRecordsToNewVersion()
          localStorage.setItem(FLU_MIGRATION_KEY, '1')
        }

        // 6. 檢查疫苗資料是否有更新，如果有則自動更新使用者記錄
        if (vaccineDefinitions.hasUpdate && appStore.babies.length > 0) {
          await handleVaccineDataUpdate()
        }

        // 6. 如果有權限且有設定提醒時間點，排程推播
        if (notificationStore.hasPermission && notificationStore.settings.timings.length > 0) {
          await initializeNotifications()
        }

        appReady.value = true

        // 初始化完成後，如果有寶寶資料且當前在首頁，導向 timeline
        if (appStore.hasOnboarded && route.path === '/') {
          router.replace('/timeline')
        }
      } catch (error: any) {
        console.error('App 初始化失敗:', error)
        // 即使失敗也讓 app 可用
        appReady.value = true
      }
    }
  },
  { immediate: true }
)

// 處理疫苗資料更新
async function handleVaccineDataUpdate() {
  try {
    console.log('🔄 偵測到疫苗資料更新，開始自動更新使用者記錄...')

    // 自動更新所有寶寶的疫苗記錄
    const updatedCount = await appStore.updateVaccineRecordsToNewVersion()

    // 清除更新標記
    vaccineDefinitions.clearUpdateFlag()

    // 如果有記錄被更新，顯示通知
    if (updatedCount > 0) {
      updateNotificationMessage.value = `疫苗資料已更新！已自動調整 ${updatedCount} 筆疫苗時間，請查看時間表。`
      showUpdateNotification.value = true

      console.log(`✅ 疫苗資料更新完成，共更新 ${updatedCount} 筆記錄`)

      // 5 秒後自動隱藏通知
      setTimeout(() => {
        showUpdateNotification.value = false
      }, 5000)

      // 重新載入記錄以反映最新狀態
      await appStore.initFromCloud()
    } else {
      console.log('✅ 疫苗資料已更新，但沒有需要調整的時間')
    }
  } catch (error) {
    console.error('❌ 疫苗資料更新失敗:', error)
    updateNotificationMessage.value = '疫苗資料更新失敗，請稍後再試或聯絡客服。'
    showUpdateNotification.value = true

    // 錯誤訊息也在 5 秒後自動隱藏
    setTimeout(() => {
      showUpdateNotification.value = false
    }, 5000)
  }
}

// 初始化推播通知
async function initializeNotifications() {
  try {
    // 使用所有寶寶的記錄，而非只有當前寶寶
    const records = appStore.allBabiesRecords

    await rescheduleAllNotifications(
      records,
      (babyId) => {
        const baby = appStore.babies.find(b => b.id === babyId)
        return baby?.nickname || '寶寶'
      },
      (vaccineId) => getVaccineDetails(vaccineId)?.name || '',
      (doseId) => getDoseDetails(doseId)?.label || '',
      notificationStore.settings.timings
    )
  } catch (error) {
    console.error('初始化推播失敗:', error)
  }
}

// 檢查並重新排程通知（如果需要）
async function checkAndRescheduleNotifications() {
  try {
    // 只在原生平台執行
    if (!Capacitor.isNativePlatform()) {
      return
    }

    // 檢查是否有權限和設定
    if (!notificationStore.hasPermission || notificationStore.settings.timings.length === 0) {
      return
    }

    // 檢查目前已排程的通知數量
    const pending = await LocalNotifications.getPending()
    const expectedCount = appStore.allBabiesRecords.filter(r => {
      const displayDate = new Date(getDisplayPlannedDate(r))
      return r.status === 'planned' && displayDate > new Date()
    }).length > 0 ? notificationStore.settings.timings.length : 0

    // 如果沒有任何已排程的通知，或數量不對，重新排程
    if (pending.notifications.length === 0 || pending.notifications.length !== expectedCount) {
      await initializeNotifications()
    }
  } catch (error) {
    console.error('檢查通知狀態失敗:', error)
  }
}

// 監聽通知事件
let appStateListener: any = null
let notificationReceivedListener: any = null

const handleAppStateChange = createNotificationPermissionResumeHandler({
  readPermission: getNotificationPermission,
  updatePermission: (permission) => notificationStore.updatePermission(permission),
  onPermissionError: (error) => {
    console.error('更新通知權限狀態失敗:', error)
  },
  onActive: () => {
    // 重新載入雲端資料（跨裝置同步）
    appStore.initFromCloud().catch(() => {})
    setTimeout(() => {
      checkAndRescheduleNotifications()
    }, 1000)
  },
})

onMounted(async () => {
  // 即時監聽 App 公告（公開資料，不需要等登入完成；App 開著時後台發布/下架會立刻反映）
  announcementStore.startListening()

  // 監聽通知被顯示（推播發送時）
  if (Capacitor.isNativePlatform()) {
    notificationReceivedListener = await LocalNotifications.addListener(
      'localNotificationReceived',
      async () => {
        // 推播發送後，延遲一小段時間後重新排程下一個疫苗
        setTimeout(async () => {
          await checkAndRescheduleNotifications()
        }, 2000)
      }
    )
  }

  // 監聽 App 從背景回到前景（同步雲端資料 + 備援通知排程）
  appStateListener = await App.addListener('appStateChange', handleAppStateChange)
})

onUnmounted(() => {
  // 清理監聽器
  announcementStore.stopListening()
  if (notificationReceivedListener) {
    notificationReceivedListener.remove()
  }
  if (appStateListener) {
    appStateListener.remove()
  }
})
</script>

<template>
  <div class="min-h-screen bg-background text-textMain antialiased">
    <!-- 載入畫面 -->
    <LoadingScreen v-if="!authReady || !appReady" />

    <!-- 全部 ready 才進 App -->
    <template v-else>
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>

      <!-- App 公告 banner -->
      <AnnouncementBanner />

      <!-- Bottom Tab Bar -->
      <BottomTabBar v-if="showTabBar" />

      <!-- 疫苗資料更新通知 -->
      <transition name="slide-up">
        <div
          v-if="showUpdateNotification"
          class="fixed bottom-20 left-4 right-4 bg-primary text-white p-4 rounded-lg shadow-lg z-50"
        >
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 text-2xl">ℹ️</div>
            <div class="flex-1">
              <p class="text-sm leading-relaxed">{{ updateNotificationMessage }}</p>
            </div>
            <button
              @click="showUpdateNotification = false"
              class="flex-shrink-0 text-white/80 hover:text-white"
            >
              ✕
            </button>
          </div>
        </div>
      </transition>
    </template>
  </div>
</template>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
